import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from "@angular/forms";
import { Router } from "@angular/router";

import { Subscription, Observable, BehaviorSubject, forkJoin } from "rxjs";
import { tap, map, finalize } from "rxjs/operators";


import { IUploadFile } from '../../../../common/services/document-management.service';
import { CanDeactivateComponent } from '../../../../common/classes/can-deactivate.component';
import { AuthService } from "../../../../services/auth.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { PermitService } from "../../../../common/services/permit.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { OnBaseKeywordMappingSearchSpec, OnBaseMappedKeyword } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import {
    LandownerProgramStatusResponse, LandownerProgramStatus, LandownerPassthroughProgramDecision, LandownerProgramApplicationResponse, LandownerPassthroughProgramApplication,
    PassthroughProgramContractor, PassthroughProgramEmergencyData, PassthroughProgramFile, PassthroughProgramLandowner, PassthroughProgramWell, PassthroughProgramInspector, LandownerPassthroughProgramNote
} from "../../../../common/models/permit.model";




@Component({
    selector: 'review-landowner-application',
    templateUrl: './review-landowner-application.component.html',
    styleUrls: ['./review-landowner-application.component.scss', '../submit-passthrough-permit/submit-passthrough-permit.component.scss'],
    standalone: false
})
export class ReviewLandownerApplicationComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    @Input() application: LandownerPassthroughProgramApplication;

    form: FormGroup;

    get dateOfLastProduction(): Date {
        if (this.wellDataForm == null) return null;
        let ctrl = this.wellDataForm.get('dateOfLastProduction');
        if (ctrl == null) return null;
        let value = ctrl.value;
        if (value == null) return null;
        return new Date(value);
    }

    get contractorAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("contractorAddress") as FormGroup;
    }

    get emergencyDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("emergencyData") as FormGroup;
    }

    get inspectorForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("inspectors") as FormGroup;
    }

    get landownerAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("landownerAddress") as FormGroup;
    }

    get programEligibleControl(): AbstractControl {
        if (this.form == null) return null;
        return this.form.get("programEligible");
    }

    get wellDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("wellData") as FormGroup;
    }

    showEmergencySection: boolean = false;
    showContractorSection: boolean = false;
    showDocumentationSection: boolean = false;
    showInspectorSection: boolean = false;
    showLandownerSection: boolean = true;
    showWellInformationSection: boolean = false;
    showWellLocationSection: boolean = false;

    watersheds: any[] = [];
    states: any[] = [];


    affidavitString: string = "Proposed Contractor's Affidavit of Insurance and Bonding";

    get affidavitCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("affidavits") as AbstractControl;
        }
        return ctrl
    }

    get affidavitFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.affidavitCtrl != null) {
            files = <IUploadFile[]>this.affidavitCtrl.value;
        }
        return files;
    }

    get applicationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("applications") as AbstractControl;
        }
        return ctrl
    }

    get applicationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.applicationCtrl != null) {
            files = <IUploadFile[]>this.applicationCtrl.value;
        }
        return files;
    }

    get contractorCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("contractorFiles") as AbstractControl;
        }
        return ctrl
    }

    get contractorFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.contractorCtrl != null) {
            files = <IUploadFile[]>this.contractorCtrl.value;
        }
        return files;
    }

    get mapCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("maps") as AbstractControl;
        }
        return ctrl
    }

    get mapFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.mapCtrl != null) {
            files = <IUploadFile[]>this.mapCtrl.value;
        }
        return files;
    }

    get packetsCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("packets") as AbstractControl;
        }
        return ctrl
    }

    get packetFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.packetsCtrl != null) {
            files = <IUploadFile[]>this.packetsCtrl.value;
        }
        return files;
    }

    get plugCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("plugPlans") as AbstractControl;
        }
        return ctrl
    }

    get plugFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.plugCtrl != null) {
            files = <IUploadFile[]>this.plugCtrl.value;
        }
        return files;
    }

    get restorationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("restorationPlans") as AbstractControl;
        }
        return ctrl
    }

    get restorationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.restorationCtrl != null) {
            files = <IUploadFile[]>this.restorationCtrl.value;
        }
        return files;
    }

    subscription: Subscription = new Subscription();

    private countySubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private townshipSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    countyObservable: Observable<string[]> = this.countySubject.asObservable();
    townshipObservable: Observable<string[]> = this.townshipSubject.asObservable();

    get countiesLoaded(): boolean {
        return this.countySubject.value.length > 0;
    }

    get townshipsLoaded(): boolean {
        return this.townshipSubject.value.length > 0;
    }

    countyLoader: Observable<boolean> = this.countyObservable.pipe(map((data: string[]) => { return data.length == 0; }));
    townshipLoader: Observable<boolean> = this.townshipObservable.pipe(map((data: string[]) => { return data.length == 0; }));


    constructor(dialog: DynamicDialogService, auth: AuthService, private builder: FormBuilder,
        private permitService: PermitService, private domainService: DomainDefinitionService, private loader: LoaderService, private documentService: DocumentManagementService,
        private router: Router) {
        super(dialog, auth);
    }

    ngOnInit() {
        this.allowDeactivate.next(this.application == null);
        this.form = this.buildForm();
        let dirty$ = this.form.statusChanges.pipe().subscribe(
            (change: any) => {
                this.allowDeactivate.next(this.form.pristine);
            },
            (error) => {
                console.error(error);
            }
        );
        this.subscription.add(dirty$);

        const stateObs$: Observable<any> = this.domainService.getValues("STATE").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.states.push(obj);
                })
            })
        )
        this.watersheds.push({ Code: "", Definition: "Not Provided" });
        const watershedObs$: Observable<any> = this.domainService.getValues("WATERSHED").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.watersheds.push(obj);
                })
            })
        )
        const county$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - County" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((counties: string[]) => {
                    counties.unshift("--");
                    this.countySubject.next(counties);
                })
            );

        const township$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - Township" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((townships: string[]) => {
                    townships.unshift("--");
                    this.townshipSubject.next(townships);
                })
            );



        const domainSubs$ = forkJoin(stateObs$, watershedObs$, county$, township$).subscribe(() => { },
            (error) => { console.error(error); });
        this.subscription.add(domainSubs$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
        super.ngOnDestroy();
    }

  

    affidavitFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.affidavitFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Contractor Affidavit of Insurance and Bonding"
            files.push(evt);
            this.affidavitCtrl.setValue(files);
        }
    }

    affidavitFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.affidavitFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.affidavitCtrl.setValue(files);
        }
    }

    applicationFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.applicationFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Plugging Application"
            files.push(evt);
            this.applicationCtrl.setValue(files);
        }
    }

    applicationFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.applicationFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.applicationCtrl.setValue(files);
        }
    }

    private buildAddressForm(obj: PassthroughProgramContractor | PassthroughProgramLandowner): FormGroup {
        return this.builder.group({
            street: [ { value: obj.streetAddress, disabled: true }],
            suite: [{ value: obj.suite, disabled: true}],
            poBox: [{ value: obj.postOfficeBox, disabled: true}],
            city: [{ value: obj.city, disabled: true}],
            state: [{ value: obj.stateCode, disabled: true}],
            zip: [{ value: obj.zipCode, disabled: true}]
        });
    }

    private buildDocumentForm(): FormGroup {
        let packets: IUploadFile[] = [];
        let applications: IUploadFile[] = [];
        let contractorFiles: IUploadFile[] = [];
        let maps: IUploadFile[] = [];
        let affidavits: IUploadFile[] = [];
        let plugPlans: IUploadFile[] = [];
        let restorationPlans: IUploadFile[] = [];
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Plugging Application Packet Received").forEach(f => packets.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Plugging Application").forEach(f => applications.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Scope of Work Offer Sheet").forEach(f => contractorFiles.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Well Location Map").forEach(f => maps.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Contractor Affidavit of Insurance and Bonding").forEach(f => affidavits.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Well Plugging Plan (Form 1-PP)").forEach(f => plugPlans.push(this.revertFile(f)));
        this.application.passthroughProgramFiles.filter(f => f.documentType === "DNR - OG - OW-LPTP Well Plugging and Restoration Agreement").forEach(f => restorationPlans.push(this.revertFile(f)));

        return this.builder.group({
            packets: [{ value: packets, disabled: true }],
            applications: [{ value: applications, disabled: true }],
            contractorFiles: [{ value: contractorFiles, disabled: true }],
            maps: [{ value: maps, disabled: true }],
            affidavits: [{ value: affidavits, disabled: true }],
            plugPlans: [{ value: plugPlans, disabled: true }],
            restorationPlans: [{ value: restorationPlans, disabled: true }]
        });
    }

    private buildEmergencyContactForm(): FormGroup {
        return this.builder.group({
            firePhone: [{ value: this.application.passthroughProgramEmergencyData.firePhoneNumber, disabled: true }],
            fireExtension: [{ value: this.application.passthroughProgramEmergencyData.fireExtension, disabled: true }],
            medicalPhone: [{ value: this.application.passthroughProgramEmergencyData.medicalPhoneNumber, disabled: true }],
            medicalExtension: [{ value: this.application.passthroughProgramEmergencyData.medicalExtension, disabled: true }],
            ingress: [{ value: this.application.passthroughProgramEmergencyData.meansOfIngress, disabled: true }]
        });
    }

    private buildInspectorForm(): FormGroup {
        return this.builder.group({
            inspectorName: [{ value: this.application.passthroughProgramInspector.inspectorName, disabled: true }],
            inspectorPhone: [{ value: this.application.passthroughProgramInspector.inspectorPhone, disabled: true }],
            backupInspectorName: [{ value: this.application.passthroughProgramInspector.backupInspectorName, disabled: true }],
            backupInspectorPhone: [{ value: this.application.passthroughProgramInspector.backupInspectorPhone, disabled: true }],
            supervisorName: [{ value: this.application.passthroughProgramInspector.supervisorName, disabled: true }],
            supervisorPhone: [{ value: this.application.passthroughProgramInspector.supervisorPhone, disabled: true }]
        });
    }

    private buildForm(): FormGroup {
        let form = this.builder.group({
            applicationId: [{ value: this.application.applicationId, disabled: true }],
            api: [{ value: this.application.wellId, disabled: true }],
            programEligible: [{ value: true, disabled: true }],
            landownerName: [{ value: this.application.passthroughProgramLandowner.ownerName, disabled: true }],
            landownerAddress: this.buildAddressForm(this.application.passthroughProgramLandowner),
            landownerPhone: [{ value: this.application.passthroughProgramLandowner.contactPhone, disabled: true }],
            landownerEmail: [{ value: this.application.passthroughProgramLandowner.emailAddress, disabled: true }],
            contractorName: [{ value: this.application.passthroughProgramContractor.contractorName, disabled: true }],
            contractorRepresentative: [{ value: this.application.passthroughProgramContractor.contractorRepresentative, disabled: true }],
            contractorEstimate: [{ value: this.application.contractorEstimate, disabled: true }],
            contractorAddress: this.buildAddressForm(this.application.passthroughProgramContractor),
            contractorOfficePhone: [{ value: this.application.passthroughProgramContractor.officePhone, disabled: true }],
            contractorCellPhone: [{ value: this.application.passthroughProgramContractor.cellPhone, disabled: true }],
            contractorEmail: [{ value: this.application.passthroughProgramContractor.emailAddress, disabled: true }],
            wellData: this.buildWellForm(),
            emergencyData: this.buildEmergencyContactForm(),
            documents: this.buildDocumentForm(),
            inspectors: this.buildInspectorForm()
        });
        return form;
    }

    private buildWellForm(): FormGroup {
        let urbanized: string = (this.application.passthroughProgramWell != null && this.application.passthroughProgramWell.urbanized != null)
            ? this.application.passthroughProgramWell.urbanized === true
                ? "Y"
                : "N"
            : "";
        return this.builder.group({
            latitude: [ { value: this.application.passthroughProgramWell.latitude, disabled: true }],
            longitude: [ { value:this.application.passthroughProgramWell.longitude, disabled: true }],
            county: [ { value:this.application.passthroughProgramWell.county, disabled: true }],
            township: [ { value:this.application.passthroughProgramWell.township, disabled: true }],
            section: [ { value:this.application.passthroughProgramWell.section, disabled: true }],
            lot: [ { value:this.application.passthroughProgramWell.lot, disabled: true }],
            tract: [ { value:this.application.passthroughProgramWell.tract, disabled: true }],
            urbanized: [{ value: urbanized, disabled: true }],
            name: [ { value:this.application.passthroughProgramWell.wellName, disabled: true }],
            number: [ { value:this.application.passthroughProgramWell.wellNumber, disabled: true }],
            api: [ { value: this.application.passthroughProgramWell.wellId, disabled: true }],
            lastOwner: [ { value:this.application.passthroughProgramWell.lastOwner, disabled: true }],
            dateOfLastProduction: [ { value:this.application.passthroughProgramWell.dateOfLastProduction, disabled: true }],
            amountLastOil: [ { value:this.application.passthroughProgramWell.amountOfLastOil, disabled: true }],
            amountLastGas: [ { value:this.application.passthroughProgramWell.amountOfLastGas, disabled: true }],
            depthPlugged: [ { value:this.application.passthroughProgramWell.depthToBePlugged, disabled: true }],
            waterSource: [ { value:this.application.passthroughProgramWell.waterSource, disabled: true }],
            watershed: [ { value:this.application.passthroughProgramWell.watershed, disabled: true }],
            amountWater: [ { value:this.application.passthroughProgramWell.amountWater, disabled: true }]
        });
    }

    composeApplication(): LandownerPassthroughProgramApplication {
        let contractor = {
            contractorName: this.form.get("contractorName").value,
            contractorRepresentative: this.form.get("contractorRepresentative").value,
            streetAddress: this.contractorAddressForm.get("street").value,
            suite: this.contractorAddressForm.get("suite").value,
            postOfficeBox: this.contractorAddressForm.get("poBox").value,
            city: this.contractorAddressForm.get("city").value,
            stateCode: this.contractorAddressForm.get("state").value,
            zipCode: this.contractorAddressForm.get("zip").value,
            officePhone: this.form.get("contractorOfficePhone").value,
            cellPhone: this.form.get("contractorCellPhone").value,
            emailAddress: this.form.get("contractorEmail").value
        } as PassthroughProgramContractor;
        let emergency = {
            firePhoneNumber: this.emergencyDataForm.get("firePhone").value,
            fireExtension: this.emergencyDataForm.get("fireExtension").value,
            medicalPhoneNumber: this.emergencyDataForm.get("medicalPhone").value,
            medicalExtension: this.emergencyDataForm.get("medicalExtension").value,
            meansOfIngress: this.emergencyDataForm.get("ingress").value
        } as PassthroughProgramEmergencyData;
        let urbanizedValue = this.wellDataForm.get("urbanized").value;
        let urbanized: boolean = null;
        if (urbanizedValue != '') {
            urbanized = urbanizedValue == 'Y' ? true : false;
        }
        let county = this.wellDataForm.get("county").value;
        let township = this.wellDataForm.get("township").value;
        let well = {
            wellId: this.form.get("api").value,
            latitude: this.wellDataForm.get("latitude").value,
            longitude: this.wellDataForm.get("longitude").value,
            county: county != '--' ? county : "",
            township: township != '--' ? township : "",
            section: this.wellDataForm.get("section").value,
            lot: this.wellDataForm.get("lot").value,
            tract: this.wellDataForm.get("tract").value,
            urbanized: urbanized,
            wellName: this.wellDataForm.get("name").value,
            wellNumber: this.wellDataForm.get("number").value,
            lastOwner: this.wellDataForm.get("lastOwner").value,
            dateOfLastProduction: this.wellDataForm.get("dateOfLastProduction").value,
            amountOfLastOil: this.wellDataForm.get("amountLastOil").value,
            amountOfLastGas: this.wellDataForm.get("amountLastGas").value,
            depthToBePlugged: this.wellDataForm.get("depthPlugged").value,
            waterSource: this.wellDataForm.get("waterSource").value,
            watershed: this.wellDataForm.get("watershed").value,
            amountWater: this.wellDataForm.get("amountWater").value
        } as PassthroughProgramWell;
        let landowner = {
            ownerName: this.form.get("landownerName").value,
            streetAddress: this.landownerAddressForm.get("street").value,
            suite: this.landownerAddressForm.get("suite").value,
            postOfficeBox: this.landownerAddressForm.get("poBox").value,
            city: this.landownerAddressForm.get("city").value,
            stateCode: this.landownerAddressForm.get("state").value,
            zipCode: this.landownerAddressForm.get("zip").value,
            contactPhone: this.form.get("landownerPhone").value,
            emailAddress: this.form.get("landownerEmail").value
        } as PassthroughProgramLandowner;
        let inspectors = {
            inspectorName: this.inspectorForm.get("inspectorName").value,
            inspectorPhone: this.inspectorForm.get("inspectorPhone").value,
            backupInspectorName: this.inspectorForm.get("backupInspectorName").value,
            backupInspectorPhone: this.inspectorForm.get("backupInspectorPhone").value,
            supervisorName: this.inspectorForm.get("supervisorName").value,
            supervisorPhone: this.inspectorForm.get("supervisorPhone").value
        } as PassthroughProgramInspector;

        let files: PassthroughProgramFile[] = [];
        this.affidavitFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.applicationFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.contractorFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.mapFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.packetFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.plugFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.restorationFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        return {
            applicationId: this.form.get("applicationId").value,
            wellId: this.form.get("api").value,
            programStatus: LandownerProgramStatus.InProcess,
            lastUpdatedByUserId: this.auth.User.id,
            lastUpdatedByUserName: this.auth.User.displayName,
            contractorEstimate: this.form.get("contractorEstimate").value,
            passthroughProgramContractor: contractor,
            passthroughProgramEmergencyData: emergency,
            passthroughProgramInspector: inspectors,
            passthroughProgramLandowner: landowner,
            passthroughProgramWell: well,
            passthroughProgramFiles: files,
            createdByUserId: this.application.createdByUserId,
            createdByUserName: this.application.createdByUserName,
            dateCreated: this.application.dateCreated,
            landownerPassthroughProgramHistories: this.application.landownerPassthroughProgramHistories
        } as LandownerPassthroughProgramApplication;
    }

    contractorFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.contractorFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Scope of Work Offer Sheet"
            files.push(evt);
            this.contractorCtrl.setValue(files);
        }
    }

    contractorFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.contractorFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.contractorCtrl.setValue(files);
        }
    }

    convertFile(data: IUploadFile): PassthroughProgramFile {
        return {
            fileName: data.fileName,
            programArea: "",
            contentType: data.type,
            documentType: data.documentType,
            documentGroup: "DNR - OG - LANDOWNER PASS-THROUGH PROGRAM",
            documentUri: data.blobUri,
            id: data.id
        } as PassthroughProgramFile;
    }

    lastProductionDateChanged(evt: Date): void {
        let lastProductionCtrl = this.wellDataForm.get("dateOfLastProduction");
        if (lastProductionCtrl != null) {
            lastProductionCtrl.setValue(evt);
        }
    }

    mapFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.mapFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Well Location Map"
            files.push(evt);
            this.mapCtrl.setValue(files);
        }
    }

    mapFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.mapFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.mapCtrl.setValue(files);
        }
    }


    onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    packetFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.packetFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Plugging Application Packet Received"
            files.push(evt);
            this.packetsCtrl.setValue(files);
        }
    }

    packetFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.packetFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.packetsCtrl.setValue(files);
        }
    }

    plugFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.plugFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Well Plugging Plan (Form 1-PP)"
            files.push(evt);
            this.plugCtrl.setValue(files);
        }
    }

    plugFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.plugFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.plugCtrl.setValue(files);
        }
    }

    restorationFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - OW-LPTP Well Plugging and Restoration Agreement"
            files.push(evt);
            this.restorationCtrl.setValue(files);
        }
    }

    restorationFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.restorationCtrl.setValue(files);
        }
    }

    revertFile(file: PassthroughProgramFile): IUploadFile {
        return {
            completed: true,
            canBeDeleted: false,
            sentToOnbase: false,
            type: file.contentType,
            blobUri: file.documentUri,
            id: file.id,
            error: false,
            isUploading: false,
            waiting: false,
            percentage: 100,
            fileName: file.fileName,
            documentType: file.documentType
        } as IUploadFile;
    }



    toggleSection(sectionName: string): void {
        switch (sectionName) {
            case "landowner":
                this.showLandownerSection = !this.showLandownerSection
                break;
            case "contractor":
                this.showContractorSection = !this.showContractorSection;
                break;
            case "wellInformation":
                this.showWellInformationSection = !this.showWellInformationSection;
                break;
            case "wellLocation":
                this.showWellLocationSection = !this.showWellLocationSection;
                break;
            case "emergency":
                this.showEmergencySection = !this.showEmergencySection;
                break;
            case "documentation":
                this.showDocumentationSection = !this.showDocumentationSection;
                break;
            case "inspector":
                this.showInspectorSection = !this.showInspectorSection;
                break;
        }
    }

}
